export const isVesselFinderTrackLink = (
  url: string | null | undefined
): boolean => !!url && url.includes("vesselfinder.com");

export interface VesselFinderIdentifiers {
  imo?: string;
  mmsi?: string;
}

const classifyVesselIdentifier = (
  identifier: string | null | undefined
): VesselFinderIdentifiers => {
  const value = identifier?.trim();
  if (/^\d{7}$/.test(value ?? "")) return { imo: value };
  if (/^\d{9}$/.test(value ?? "")) return { mmsi: value };
  return {};
};

/** Extract and classify an IMO or MMSI from a VesselFinder URL or raw ID. */
export const getVesselFinderIdentifiers = (
  source: string
): VesselFinderIdentifiers => {
  if (!source?.trim()) return {};

  const directIdentifier = classifyVesselIdentifier(source);
  if (directIdentifier.imo || directIdentifier.mmsi) {
    return directIdentifier;
  }

  try {
    const urlObj = new URL(source);

    const imoFromQuery = urlObj.searchParams.get("imo");
    const parsedImo = classifyVesselIdentifier(imoFromQuery);
    if (parsedImo.imo) return parsedImo;

    const mmsiFromQuery = urlObj.searchParams.get("mmsi");
    const parsedMmsi = classifyVesselIdentifier(mmsiFromQuery);
    if (parsedMmsi.mmsi) return parsedMmsi;

    const detailsMatch = urlObj.pathname.match(/\/vessels\/details\/(\d+)/);
    if (detailsMatch?.[1]) {
      return classifyVesselIdentifier(detailsMatch[1]);
    }

    const imoPathMatch = urlObj.pathname.match(/\/vessels\/.*-imo-(\d+)/i);
    if (imoPathMatch?.[1]) {
      const parsedPathImo = classifyVesselIdentifier(imoPathMatch[1]);
      return parsedPathImo.imo ? parsedPathImo : {};
    }
  } catch {
    const parameterMatch = source.match(/[?&](imo|mmsi)=(\d+)/i);
    if (parameterMatch?.[2]) {
      const identifiers = classifyVesselIdentifier(parameterMatch[2]);
      if (parameterMatch[1].toLowerCase() === "imo") {
        return identifiers.imo ? identifiers : {};
      }
      return identifiers.mmsi ? identifiers : {};
    }
  }

  return {};
};

/** Backward-compatible ID accessor for existing map call sites. */
export const getVesselFinderId = (source: string): string | undefined => {
  const { imo, mmsi } = getVesselFinderIdentifiers(source);
  return imo ?? mmsi;
};
